import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { databaseService } from '../../services/database.service';
import { useLanguage } from '../../hooks/useLanguage';
import { i18nService } from '../../services/i18n.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Card from '../../components/common/Card';

const AboutPage = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [aboutData, setAboutData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await databaseService.getAboutPage();
        const members = await databaseService.getTeamMembers();
        setAboutData(data);
        setTeamMembers(members);
      } catch (error) {
        console.error('Error fetching about data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="py-12">
      {/* Company Story */}
      <section className="container mx-auto px-4 mb-16">
        <h1 className="text-4xl font-display font-bold text-center mb-8">{t('about.title')}</h1>
        <div className="max-w-4xl mx-auto">
          {i18nService.getTranslatedField(aboutData, 'companyStory', currentLanguage) || aboutData?.companyStory ? (
            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{
                __html: i18nService.getTranslatedField(aboutData, 'companyStory', currentLanguage) || aboutData.companyStory
              }}
            />
          ) : (
            <p className="text-lg text-gray-600 text-center">
              {t('about.defaultStory')}
            </p>
          )}
        </div>
      </section>

      {/* Mission */}
      {(i18nService.getTranslatedField(aboutData, 'mission', currentLanguage) || aboutData?.mission) && (
        <section className="bg-primary-50 py-12 mb-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-display font-bold mb-4">{t('about.missionTitle')}</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {i18nService.getTranslatedField(aboutData, 'mission', currentLanguage) || aboutData.mission}
            </p>
          </div>
        </section>
      )}

      {/* Vision */}
      {(i18nService.getTranslatedField(aboutData, 'vision', currentLanguage) || aboutData?.vision) && (
        <section className="container mx-auto px-4 mb-16">
          <div className="text-center">
            <h2 className="text-3xl font-display font-bold mb-4">{t('about.visionTitle')}</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {i18nService.getTranslatedField(aboutData, 'vision', currentLanguage) || aboutData.vision}
            </p>
          </div>
        </section>
      )}

      {/* Team Members */}
      {teamMembers.length > 0 && (
        <section className="container mx-auto px-4 mb-16">
          <h2 className="text-3xl font-display font-bold text-center mb-8">{t('about.teamTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="text-center">
                {member.photo && (
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                  />
                )}
                <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                <p className="text-primary-600 font-medium mb-2">
                  {i18nService.getTranslatedField(member, 'role', currentLanguage) || member.role}
                </p>
                {(i18nService.getTranslatedField(member, 'bio', currentLanguage) || member.bio) && (
                  <p className="text-gray-600">
                    {i18nService.getTranslatedField(member, 'bio', currentLanguage) || member.bio}
                  </p>
                )}
              </Card>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AboutPage;
